import { useState } from 'react';
import type { RetailerLeadsService } from '@beautyburst/leads';
import type { CustomerApiClient } from '../api-client.ts';
import { Button, ErrorSummary } from '@beautyburst/ui';
import {
  FormField,
  errorsForSummary,
  useFormErrors,
  validateEmail,
  validatePhone,
} from '../forms.tsx';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';

export interface RetailerLeadFields {
  contactName: string;
  businessName: string;
  email: string;
  phone: string;
  city: string;
  region: string;
  locationCount: string;
  posSystem: string;
  inventorySystem: string;
  interestNotes: string;
  consentMarketing: boolean;
}

/** Kept separate so the payload can be verified never to contain onboarding state. */
export function buildRetailerLeadPayload(
  fields: RetailerLeadFields,
): Parameters<RetailerLeadsService['submit']>[0] {
  return {
    contactName: fields.contactName.trim(),
    businessName: fields.businessName.trim(),
    email: fields.email.trim(),
    phone: fields.phone.trim() || null,
    city: fields.city.trim() || null,
    region: fields.region.trim() || null,
    locationCount: fields.locationCount === '' ? null : Number(fields.locationCount),
    posSystem: fields.posSystem.trim() || null,
    inventorySystem: fields.inventorySystem.trim() || null,
    interestNotes: fields.interestNotes.trim() || null,
    leadSource: 'PARTNER_PAGE',
    // `consent_contact` is the established first-party lead record's explicit
    // consent field. This page uses it only for the optional marketing opt-in,
    // not as a condition of submitting a founder-review lead.
    consentContact: fields.consentMarketing,
    consentAt: fields.consentMarketing ? new Date() : null,
  };
}

export interface PartnerScreenProps {
  client: { readonly leads: Pick<CustomerApiClient['leads'], 'submitRetailer'> };
}

/** Partnership interest capture only; submitting never approves or onboards a retailer. */
export function PartnerScreen({ client }: PartnerScreenProps) {
  const [fields, setFields] = useState<RetailerLeadFields>({
    contactName: '',
    businessName: '',
    email: '',
    phone: '',
    city: '',
    region: '',
    locationCount: '',
    posSystem: '',
    inventorySystem: '',
    interestNotes: '',
    consentMarketing: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const { errors, setErrors, clear } = useFormErrors();
  const update = <Key extends keyof RetailerLeadFields>(
    key: Key,
    value: RetailerLeadFields[Key],
  ) => {
    setFields((current) => ({ ...current, [key]: value }));
    clear(`partner-${key}`);
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = {
      ...(fields.contactName.trim() === '' ? { 'partner-contactName': 'Enter your name.' } : {}),
      ...(fields.businessName.trim() === ''
        ? { 'partner-businessName': 'Enter your business name.' }
        : {}),
      ...(validateEmail(fields.email) === undefined
        ? {}
        : { 'partner-email': validateEmail(fields.email)! }),
      ...(validatePhone(fields.phone) === undefined
        ? {}
        : { 'partner-phone': validatePhone(fields.phone)! }),
    };
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    const payload = buildRetailerLeadPayload(fields);
    try {
      await client.leads.submitRetailer(
        payload as unknown as Parameters<CustomerApiClient['leads']['submitRetailer']>[0],
      );
      setSubmitted(true);
    } catch {
      setErrors({
        'partner-email': 'We could not submit your partnership interest. Please try again.',
      });
    }
  };

  if (submitted) {
    return (
      <section aria-labelledby="partner-title">
        <h1
          id="partner-title"
          style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
        >
          Own a Beauty Supply Store? Partner With Beauty Burst.
        </h1>
        <p role="status">
          Thanks — your partnership interest was recorded for review. Submission does not approve a
          retailer.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="partner-title">
      <h1
        id="partner-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
      >
        Own a Beauty Supply Store? Partner With Beauty Burst.
      </h1>
      <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary }}>
        Tell us about your business. A submitted interest is reviewed before any partnership
        decision.
      </p>
      <form
        noValidate
        onSubmit={(event) => void submit(event)}
        style={{ display: 'grid', gap: space[4] }}
      >
        <ErrorSummary errors={errorsForSummary(errors)} />
        <FormField
          id="partner-contactName"
          label="Owner or contact name"
          error={errors['partner-contactName']}
          required
        >
          <input
            value={fields.contactName}
            onChange={(event) => update('contactName', event.currentTarget.value)}
          />
        </FormField>
        <FormField
          id="partner-businessName"
          label="Business name"
          error={errors['partner-businessName']}
          required
        >
          <input
            value={fields.businessName}
            onChange={(event) => update('businessName', event.currentTarget.value)}
          />
        </FormField>
        <FormField
          id="partner-email"
          label="Email address"
          error={errors['partner-email']}
          required
        >
          <input
            type="email"
            value={fields.email}
            onChange={(event) => update('email', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-phone" label="Phone number" error={errors['partner-phone']}>
          <input
            type="tel"
            value={fields.phone}
            onChange={(event) => update('phone', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-city" label="City">
          <input
            value={fields.city}
            onChange={(event) => update('city', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-region" label="State">
          <input
            value={fields.region}
            onChange={(event) => update('region', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-locationCount" label="Number of locations">
          <input
            type="number"
            min="1"
            value={fields.locationCount}
            onChange={(event) => update('locationCount', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-posSystem" label="Current POS system (optional)">
          <input
            value={fields.posSystem}
            onChange={(event) => update('posSystem', event.currentTarget.value)}
          />
        </FormField>
        <FormField id="partner-inventorySystem" label="Current inventory system (optional)">
          <input
            value={fields.inventorySystem}
            onChange={(event) => update('inventorySystem', event.currentTarget.value)}
          />
        </FormField>
        <label
          htmlFor="partner-interestNotes"
          style={{ display: 'grid', gap: space[2], ...typeStyle('label') }}
        >
          Interest notes
          <textarea
            id="partner-interestNotes"
            value={fields.interestNotes}
            onChange={(event) => update('interestNotes', event.currentTarget.value)}
          />
        </label>
        <label style={{ display: 'flex', gap: space[2], minHeight: '44px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={fields.consentMarketing}
            onChange={(event) => update('consentMarketing', event.currentTarget.checked)}
          />
          I would like to receive Beauty Burst marketing updates. This is optional and is not
          required to submit my partnership interest.
        </label>
        <Button type="submit">Submit partnership interest</Button>
      </form>
    </section>
  );
}
